import type { OpenLoan, PayoffLetterRequest, PayoffLetterResponse } from "@credit-acceptance/step6-bff-client";
import { useMutation } from "@tanstack/react-query";

import { payoffApi } from "../../../api/client";

export interface PayoffLetterInput {
  payoffDate?: string;
  quoteMileage?: number;
}

/**
 * Maps the fields shared by the payoff and settlement letter requests:
 * the analyst-entered date/mileage override plus the loan's identifiers and
 * GAP/VSC flags. `hasGap`/`hasVsc` rename from the loan's `isHasGap`/`isHasVsc`;
 * `isGapThirdParty`/`isVscThirdParty` keep their name. All four default to
 * `false` when the loan omits them.
 * @param {OpenLoan} loan - The selected open-loan entry.
 * @param {PayoffLetterInput} input - The analyst-entered payoff date/mileage override.
 * @returns {PayoffLetterRequest} The mapped shared fields.
 */
export function buildCommonLetterRequestFields(loan: OpenLoan, input: PayoffLetterInput): PayoffLetterRequest {
  return {
    payoffDate: input.payoffDate || undefined,
    quoteMileage: input.quoteMileage,
    creditApplicationId: loan.creditApplicationId,
    hasGap: loan.isHasGap ?? false,
    isGapThirdParty: loan.isGapThirdParty ?? false,
    hasVsc: loan.isHasVsc ?? false,
    isVscThirdParty: loan.isVscThirdParty ?? false,
    customerId: loan.customerId,
  };
}

/**
 * Maps a selected `OpenLoan` entry plus the analyst-entered date/mileage
 * override to a `PayoffLetterRequest`.
 * @param {OpenLoan} loan - The selected open-loan entry.
 * @param {PayoffLetterInput} input - The analyst-entered payoff date/mileage override.
 * @returns {PayoffLetterRequest} The mapped request.
 */
export function buildPayoffLetterRequest(loan: OpenLoan, input: PayoffLetterInput): PayoffLetterRequest {
  return buildCommonLetterRequestFields(loan, input);
}

/**
 * Generates a payoff letter for a loan. Never caches or dedups the returned
 * `documentId` — every click issues a fresh request (caps2 parity).
 * @returns {ReturnType<typeof useMutation<PayoffLetterResponse, unknown, PayoffLetterRequest>>} The mutation.
 */
export function useGeneratePayoffLetter() {
  return useMutation({
    mutationFn: async (request: PayoffLetterRequest): Promise<PayoffLetterResponse> => {
      const response = await payoffApi.generatePayoffLetter(request);
      return response.data;
    },
  });
}
